// Copyright (c) Docugami, Inc. All rights reserved.

import { authAccessor } from "authentication/authAccessor";
import axios, { AxiosRequestConfig } from "axios";
import { RequestDisabledError } from "networking/RequestDisabledError";
import { useEffect } from "react";
import { selectIsLimitedOrganization } from "store/AccountInfoHelpers";
import { TelemetryCategory, TelemetryExceptions } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { useDgAuth } from "./DgAuthentication";
import { store } from "store";

const isDgUrl = (url?: string) =>
    !!url?.includes('docugami.com') || !!url?.includes('localhost')

export const useAuthenticateRequests = (excludedUrls?: string[]) => {
    const { getAccessTokenSilently } = useDgAuth();

    // NOTE: Because this hook is modifying the global axios instance, do not add additional dependencies that could cause its
    // behavior to de-sync from consumers that may be further down in the DOM (since hooks run from child nodes up through the hierarchy).
    useEffect(() => {
        authAccessor.setAccessToken(getAccessTokenSilently);

        const onRequest = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
            const isAuthenticated = excludedUrls?.findIndex(url => config.url?.startsWith(url)) === -1 && isDgUrl(config.url);
            if (!isAuthenticated) {
                return config;
            }

            const token = await getAccessTokenSilently();
            config.headers.common['Authorization'] = `Bearer ${token}`;
            const organizationId = store.getState().organizations.currentOrganization?.id;
            if (!!organizationId) {
                config.headers.common['x-docugami-organization-id'] = organizationId;
            }

            return config;
        };

        const onReponseError = async (error: any): Promise<any> => {
            if (error instanceof RequestDisabledError) {
                if (error.track) {
                    Telemetry.trackException(error, { name: TelemetryExceptions.RequestDisabled, url: error.url, baseUrl: error.baseUrl });
                }
            }
            else if (axios.isAxiosError(error)) {
                const isUnauthorizedRequest = error.response?.status === 401;
                const isForbiddenRequest = error.response?.status === 403;
                if ((isUnauthorizedRequest || isForbiddenRequest) && !((error.config as any).__isAuthRetryRequest)) {
                    (error.config as any).__isAuthRetryRequest = true;
                    try {
                        const isLimitedOrganization = selectIsLimitedOrganization(store.getState())
                        const shouldForceRefetch = (isForbiddenRequest && !isLimitedOrganization) || isUnauthorizedRequest;
                        const token = await getAccessTokenSilently(shouldForceRefetch ? { ignoreCache: true } : undefined);
                        const config = error.config;
                        config.headers.Authorization = `Bearer ${token}`;
                        return axios.request(config);
                    }
                    catch {
                    }
                } else {
                    Telemetry.trackException(error, { category: TelemetryCategory.Network, url: error.config.url });
                }
            }

            throw error;
        }

        const unsubscribeRequest = axios.interceptors.request.use(onRequest, onReponseError);
        const unsubscribeResponse = axios.interceptors.response.use(undefined, onReponseError);
        return () => {
            axios.interceptors.request.eject(unsubscribeRequest);
            axios.interceptors.response.eject(unsubscribeResponse);
        }
    },
    [excludedUrls, getAccessTokenSilently])
}
